import type { JSX } from 'solid-js'
import { createMemo, mergeProps, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import type { BadgePropsType } from '~/types/Badge'

const variantClasses: Record<string, Record<string, string>> = {
  contained: {
    primary: 'bg-primary-500 text-white',
    secondary: 'bg-slate-500 text-white',
    success: 'bg-green-500 text-white',
    danger: 'bg-red-500 text-white',
    warning: 'bg-amber-500 text-white',
    info: 'bg-blue-500 text-white'
  },
  outlined: {
    primary: 'border border-primary-500 text-primary-500',
    secondary: 'border border-slate-500 text-slate-500',
    success: 'border border-green-500 text-green-500',
    danger: 'border border-red-500 text-red-500',
    warning: 'border border-amber-500 text-amber-500',
    info: 'border border-blue-500 text-blue-500'
  },
  soft: {
    primary: 'bg-primary-50 text-primary dark:bg-primary-500',
    secondary: 'bg-slate-50 text-foreground-subtle dark:bg-slate-500',
    success: 'bg-green-50 text-success dark:bg-green-500',
    danger: 'bg-red-50 text-danger dark:bg-red-500',
    warning: 'bg-amber-50 text-warning dark:bg-amber-500',
    info: 'bg-blue-50 text-info dark:bg-blue-500',
    default: 'bg-slate-100 text-foreground dark:bg-slate-700/70'
  }
}

const sizeClasses: Record<string, { dot: string; badge: string }> = {
  sm: {
    dot: 'w-2 h-2',
    badge: 'h-4 min-w-[1rem] px-1 text-[0.65rem] leading-none'
  },
  md: {
    dot: 'w-2.5 h-2.5',
    badge: 'h-5 min-w-[1.25rem] px-1.5 text-xs leading-none'
  },
  lg: {
    dot: 'w-3 h-3',
    badge: 'h-6 min-w-[1.5rem] px-2 text-sm leading-none'
  }
}

const positionClasses: Record<string, string> = {
  'top-right': '-top-2 -right-2',
  'top-left': '-top-2 -left-2',
  'bottom-right': '-bottom-2 -right-2',
  'bottom-left': '-bottom-2 -left-2'
}

export default function Badge(props: BadgePropsType): JSX.Element {
  const merged = mergeProps(
    {
      color: 'primary',
      variant: 'contained',
      size: 'md',
      position: 'top-right',
      dot: false,
      max: 99,
      showZero: false
    },
    props as Partial<BadgePropsType>
  )

  const [local, others] = splitProps(merged, ['color', 'variant', 'size', 'count', 'dot', 'max', 'showZero', 'position', 'children', 'class', 'classList'])

  const showBadge = () => {
    if (local.dot) return true
    if (typeof local.count === 'undefined') return false
    if (local.count === 0) return local.showZero
    return true
  }

  const badgeContent = () => {
    if (local.dot) return null
    if (typeof local.count === 'undefined') return null
    return local.count > (local.max || 99) ? `${local.max}+` : local.count
  }

  const badgeClasses = createMemo(() => {
    const variantStyle = variantClasses[local.variant][local.color]
    const sizeStyle = local.dot ? sizeClasses[local.size].dot : sizeClasses[local.size].badge

    return twMerge('inline-flex items-center justify-center rounded-full font-medium', sizeStyle, variantStyle, local.class)
  })

  const badgePositionClasses = createMemo(() => twMerge('absolute inline-flex items-center justify-center', positionClasses[local.position], local.class))

  return (
    <div>
      <div class="relative inline-flex badge-container flex-nowrap">
        {local.children}
        {showBadge() && (
          <span class={badgePositionClasses()} classList={local.classList} {...others}>
            <span class={badgeClasses()}>{badgeContent()}</span>
          </span>
        )}
      </div>
    </div>
  )
}
